<template>
  <v-stepper-content step="1">
    <v-card flat class="mb-4">
      <v-card-text>
        <!-- Cuando no hay método seleccionado, mostrar solo los selectores principales -->
        <div v-if="!transportMethod" class="text-center py-6">
          <v-row justify="center">
            <v-col cols="12" md="8" class="text-center">
              <h3 class="headline mb-5">{{ (status && (status.status !== 'created' && status.status !== 'free')) ? 'Seleccione el tipo de operación y transporte' : 'Seleccione el tipo de transporte' }}</h3>
              <v-select
                v-if="status.status !== 'created' && !isRecepcionar"
                :value="movementType"
                :items="movementTypeOptions"
                label="TIPO DE OPERACIÓN"
                outlined
                required
                :disabled="isAssignment || isMovement"
                prepend-inner-icon="mdi-sync"
                hide-details="auto"
                class="mb-4"
                @input="$emit('update:movementType', $event)"
              ></v-select>
              <v-select
                v-model="localTransportMethod"
                :items="availableTransportOptions"
                :label="!isNewMove ? '¿QUIÉN HA REALIZADO EL TRANSPORTE?' : '¿QUIÉN REALIZARÁ EL TRANSPORTE?'"
                outlined
                required
                prepend-inner-icon="mdi-account-question"
                :rules="validations.transportation"
                :error-messages="errors.transportation"
                hide-details="auto"
                @input="handleTransportMethodChange"
              ></v-select>
            </v-col>
          </v-row>
        </div>

        <!-- Mostrar todos los campos cuando ya hay un método seleccionado -->
        <div v-else>
          <v-row>
            <v-col cols="12" md="6">
              <v-select 
                v-model="localTransportMethod" 
                :items="availableTransportOptions"
                :label="!isNewMove ? '¿QUIÉN HA REALIZADO EL TRANSPORTE?' : '¿QUIÉN REALIZARÁ EL TRANSPORTE?'" 
                outlined 
                required
                prepend-inner-icon="mdi-account-question" 
                :rules="validations.transportation"
                :error-messages="errors.transportation" 
                hide-details="auto"
                @input="handleTransportMethodChange"
              />
            </v-col>

            <v-col cols="12" md="6" v-if="transportMethod === 'GRUA'">
              <grua-selector
                :value="selectedGrua"
                :current-dealer-id="currentDealerId"
                :last-movement="lastMovement"
                :is-completing-movement="isCompletingMovement"
                :required="false"
                @grua-created="$emit('grua-created', $event)"
                @input="$emit('update:selectedGrua', $event)"
              />
            </v-col>

            <v-col cols="12" md="6" v-if="showDriverSelector">
              <driver-selector
                :value="selectedDriver"
                :current-dealer-id="currentDealerId"
                :transport-method="transportMethod"
                :last-movement="lastMovement"
                :current-user="currentUser"
                :first="step=true"
                label="¿QUIÉN ES EL CONDUCTOR?"
                :hint="'Para buscar el conductor es necesario añadir el número del DNI'"
                :persistent-hint="true"
                @driver-created="$emit('driver-created', $event)"
                @input="$emit('update:selectedDriver', $event)"
              />
              <v-alert 
                v-if="selectedDriver && selectedDriver.document_expiration_date && new Date(selectedDriver.document_expiration_date) < new Date()" 
                type="warning" 
                class="mt-2" 
                dense 
                text
              >
                <div class="d-flex align-center">
                  <div>La licencia de conducción está caducada. Por favor, seleccione otro conductor.</div>
                </div>
              </v-alert>
            </v-col>
          </v-row>

          <!-- Sección de fecha y hora -->
          <v-row>
            <v-col cols="12">
              <v-card flat class="mb-4">
                <v-row>
                  <v-col cols="12" md="6">
                    <div class="subtitle-1 font-weight-bold mb-4">
                      <v-icon left color="primary">mdi-calendar-clock</v-icon>
                      Fecha
                    </div>
                    <custom-date-time-picker
                      :value="datetime"
                      label="Fecha y hora del movimiento"
                      :rules="validations.datetime"
                      :isAssignment="isAssignment"
                      :lastMovement="lastMovement"
                      :movements="status ? status.movements : []"
                      :error-messages="errors.datetime"
                      prepend-inner-icon="mdi-calendar-clock"
                      dense
                      @input="$emit('update:datetime', $event)"
                    />
                  </v-col>
                  <v-col cols="12" md="6" v-if="showEndDate">
                    <div class="subtitle-1 font-weight-bold mb-4">
                      <v-icon left color="primary">mdi-calendar-clock</v-icon>
                      Fecha de devolución
                    </div>
                    <custom-date-time-picker
                      :value="endDate"
                      :min="datetime || undefined"
                      label="Fecha de devolución"
                      :rules="validations.datetime"
                      :isAssignment="isAssignment"
                      :isMovement="isMovement"
                      :lastMovement="lastMovement"
                      :movements="status ? status.movements : []"
                      :error-messages="errors.datetime"
                      prepend-inner-icon="mdi-calendar-clock"
                      dense
                      @input="$emit('update:endDate', $event)"
                    />
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>

          <!-- Sección de ubicaciones -->
          <v-row>
            <v-col cols="12" :md="showFinalLocation ? 6 : 12">
              <v-select
                v-model="localInitialLocation"
                :items="locationOptions"
                item-text="displayName"
                item-value="id"
                label="Ubicación"
                outlined
                required
                prepend-inner-icon="mdi-map-marker"
                :rules="validations.source_location_id"
                :error-messages="errors.source_location_id"
                @input="$emit('update:initialLocation', $event)"
              />
            </v-col>
            <v-col v-if="showFinalLocation && !isRecepcionar" cols="12" md="6">
              <v-select
                v-model="localFinalLocation"
                :items="locationOptions"
                item-text="displayName"
                item-value="id"
                label="Ubicación de destino"
                outlined
                required
                prepend-inner-icon="mdi-map-marker-check"
                :rules="validations.destination_location_id"
                :error-messages="errors.destination_location_id"
                @input="$emit('update:finalLocation', $event)"
              />
            </v-col>
          </v-row>

          <!-- Información adicional -->
          <v-row v-if="status && status.status === 'registered'">
            <v-col cols="12">
              <v-expansion-panels flat>
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <div>
                      <v-icon small color="primary" class="mr-2">mdi-information-outline</v-icon>
                      <span class="font-weight-medium">Información adicional (opcional)</span>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-textarea
                      v-model="localAdditionalInfo"
                      label="Información adicional"
                      outlined
                      rows="3"
                      placeholder="Agregue cualquier información adicional sobre el movimiento..."
                      hide-details="auto"
                      counter
                    />
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-col>
          </v-row>
          <!-- Información para recepciones -->
          <!-- <v-alert v-if="isReception" type="info" class="mt-3" dense text>
            <div class="d-flex align-center">
              <v-icon left color="info">mdi-information-outline</v-icon>
              <div>
                <strong>Recepción de vehículo:</strong> Solo se requiere la ubicación de origen donde se recibe el vehículo.
              </div>
            </div>
          </v-alert> -->
        </div>
      </v-card-text>
    </v-card>

    <div class="d-flex justify-end">
      <v-btn
        v-if="!isAssignment"
        :disabled="!canProceedToNext"
        :color="(isLastMovementEntry || hasExistingMovements) ? 'success' : 'primary'"
        @click="handleButtonClick"
      >
        <v-icon left>{{ buttonIcon }}</v-icon>
        {{ buttonText }}
      </v-btn>
      <div v-if="isAssignment">
        <v-btn
          v-if="isLastMovementEntry || hasExistingMovements"
          :disabled="!canProceedToNext"
          color="warning"
          @click="showConfirmDialog = true"
        >
          Entregar
        </v-btn>
        <v-btn
          :disabled="!canProceedToNext"
          class="ml-2"
          :color="(isLastMovementEntry || hasExistingMovements) ? 'success' : 'primary'"
          @click="handleButtonClick"
        >
          <v-icon left>{{ buttonIcon }}</v-icon>
          {{ buttonTextAssignment }}
        </v-btn>
      </div>
    </div>

    <!-- Diálogo de confirmación para entregar cesión -->
    <v-dialog v-model="showConfirmDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="headline warning white--text">
          <v-icon left color="white">mdi-alert</v-icon>
          Atención
        </v-card-title>
        <v-card-text class="pt-4">
          <p class="text-body-1">¿Está seguro que desea entregar esta cesión?</p>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer></v-spacer>
          <v-btn text @click="showConfirmDialog = false">
            Cancelar
          </v-btn>
          <v-btn color="warning" @click="confirmEntregar">
            Continuar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-stepper-content>
</template>

<script>
import { mapGetters } from 'vuex'
import DriverSelector from './DriverSelector.vue'
import GruaSelector from './GruaSelector.vue'
import CustomDateTimePicker from './CustomDateTimePicker.vue'
import MovimientoService from '@/services/MovimientoService'
import DriverService from '@/services/DriverService'

export default {
  name: 'TransportMethodStep',
  components: {
    DriverSelector,
    GruaSelector,
    CustomDateTimePicker
  },
  props: {
    transportMethod: {
      type: String,
      default: ''
    },
    isRecepcionar: {
      type: Boolean,
      default: false
    },
    movementType: {
      type: String,
      default: ''
    },
    selectedDriver: {
      type: Object,
      default: null
    },
    selectedGrua: {
      type: Object,
      default: null
    },
    datetime: {
      type: [String, Number],
      default: null
    },
    endDate: {
      type: [String, Number],
      default: null
    },
    initialLocation: {
      type: [String, Number],
      default: ''
    },
    finalLocation: {
      type: [String, Number],
      default: ''
    },
    additionalInfo: {
      type: String,
      default: ''
    },
    isAssignment: {
      type: Boolean,
      default: false
    },
    isMovement: {
      type: Boolean,
      default: false
    },
    locationOptions: {
      type: Array,
      default: () => []
    },
    availableTransportOptions: {
      type: Array,
      default: () => []
    },
    movementTypeOptions: {
      type: Array,
      default() {
        // Si es assignment, solo mostrar cesión y movimiento deshabilitado
        if (this.isAssignment) {
          return [
            { text: 'Movimiento', value: 'move', selected: true },
            { text: 'Cesión', value: 'assignment' }
          ]
        }
        if (this.isMovement) {
          return [
            { text: 'Movimiento', value: 'move' },
            { text: 'Cesión', value: 'assignment', selected: true }
          ]
        }
        return [
          { text: 'Movimiento', value: 'move' },
          { text: 'Cesión', value: 'assignment' }
        ]
      }
    },
    validations: {
      type: Object,
      default: () => ({})
    },
    errors: {
      type: Object,
      default: () => ({})
    },
    currentDealerId: {
      type: [String, Number],
      default: null
    },
    lastMovement: {
      type: Object,
      default: null
    },
    isProcessing: {
      type: Boolean,
      default: false
    },
    isCompletingMovement: {
      type: Boolean,
      default: false
    },
    isNewMove: {
      type: Boolean,
      default: false
    },
    currentUser: {
      type: Object,
      required: true
    },
    status: {
      type: Object,
      default: null
    },
    isLastMovementEntry: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      localTransportMethod: this.transportMethod,
      localAdditionalInfo: this.additionalInfo,
      localInitialLocation: this.initialLocation || null,
      localFinalLocation: this.finalLocation || null,
      showConfirmDialog: false
    }
  },
  computed: {
    ...mapGetters(['HAS_PERMISSION']),
    showDriverSelector() {
      // No mostrar para GRUA ya que tiene su propio selector
      if (!this.transportMethod || this.transportMethod === 'GRUA') {
        return false
      }
      
      // Para nuevos movimientos (sin lastMovement), mostrar siempre excepto para GRUA
      if (!this.lastMovement) {
        return true
      }
      
      // Para YO MISMO, mostrar siempre (se preseleccionará automáticamente el conductor del usuario)
      if (this.transportMethod === 'YO MISMO') {
        return true
      }
      
      // Para DRIVER y CLIENTE, mostrar siempre excepto si es entrada completada
      if (this.transportMethod === 'DRIVER' || this.transportMethod === 'CLIENTE') {
        // No mostrar si es una entrada ya completada
        // if (this.lastMovement.type === 'entry' && this.lastMovement.status === 'completed') {
        //   return false
        // }
        return true
      }
      
      // Para otros métodos de transporte, mostrar por defecto
      return true
    },
    showEndDate() {
      return this.movementType === 'assignment' || this.movementType === 'move' || 
             (this.transportMethod === 'CLIENTE' && this.status && (this.status.status !== 'created' && this.status.status !== 'free'))
    },
    showFinalLocation() {
      // Ocultar ubicación final en recepciones (cuando no hay movimientos anteriores) y cuando isRecepcionar es true
      return !(this.status && this.status.status === 'created' && 
               (!this.status.movements || this.status.movements.length === 0))
    },
    isReception() {
      // Es una recepción si el vehículo no tiene movimientos anteriores
      return this.status && this.status.status === 'created' && 
             (!this.status.movements || this.status.movements.length === 0)
    },
    hasExistingMovements() {
      // Tiene movimientos anteriores
      return this.status && this.status.movements && this.status.movements.length > 0
    },
    buttonText() {
      if (this.status && this.status.status === 'free') {
        return 'Siguiente'
      }
      // Mostrar "Completar" si es la última entrada del movimiento o hay movimientos anteriores
      if (this.isLastMovementEntry || this.hasExistingMovements) {
        return 'Completar'
        // return 'Completar movimiento'
      }
      return 'Siguiente'
    },
    buttonTextAssignment() {
      if (this.status && this.status.status === 'free') {
        return 'Siguiente'
      }
      // Mostrar "Completar" si es la última entrada del movimiento o hay movimientos anteriores
      if (this.isLastMovementEntry || this.hasExistingMovements) {
        return 'Reservar'
      }
      return 'Siguiente'
    },
    buttonIcon() {
      return (this.isLastMovementEntry || this.hasExistingMovements) ? 'mdi-check' : 'mdi-arrow-right'
    },
    canProceedToNext() {
      const basicValidation = this.transportMethod && 
                              this.initialLocation && 
                              this.datetime &&
                              (!this.showEndDate || this.endDate)
      
      // Para recepciones, validar ubicación final según isRecepcionar
      const locationValidation = (this.isReception || this.isRecepcionar) ? true : this.finalLocation
      
      // Si el método de transporte es GRUA, es OBLIGATORIO seleccionar una grúa
      if (this.transportMethod === 'GRUA') {
        return basicValidation && locationValidation
      }
      
      // Para DRIVER, CLIENTE u otros métodos, el conductor es OPCIONAL
      return basicValidation && locationValidation
    }
  },
  watch: {
    transportMethod: {
      handler(newValue) {
        this.localTransportMethod = newValue
        
        // Cuando se selecciona "YO MISMO", buscar y preseleccionar el conductor del usuario actual
        if (newValue === 'YO MISMO' && this.currentUser && this.currentUser.email) {
          // Buscar el conductor que tenga el mismo email que el usuario logueado
          DriverService.get({ 
            filter: { email: this.currentUser.email },
            expand: 'dealer'
          }).then(({ data }) => {
            if (data && data.data && data.data.length > 0) {
              const driver = data.data[0]
              this.$emit('update:selectedDriver', driver)
            } else {
              console.warn('⚠️ [TransportMethodStep] No se encontró conductor con email:', this.currentUser.email)
            }
          }).catch(err => {
            console.error('❌ [TransportMethodStep] Error buscando conductor para "YO MISMO":', err)
          })
        }
      },
      immediate: true
    },
    initialLocation: {
      handler(newValue) {
        if (newValue !== undefined && newValue !== null && newValue !== '' && newValue !== this.localInitialLocation) {
          this.localInitialLocation = newValue
        }
      },
      immediate: true
    },
    finalLocation: {
      handler(newValue) {
        if (newValue !== undefined && newValue !== null && newValue !== '' && newValue !== this.localFinalLocation) {
          this.localFinalLocation = newValue
        }
      },
      immediate: true
    },
    additionalInfo(newVal) {
      this.localAdditionalInfo = newVal
    },
    localAdditionalInfo(newVal) {
      this.$emit('update:additionalInfo', newVal)
    }
  },
  methods: {
    handleTransportMethodChange(value) {
      this.localTransportMethod = value
      this.$emit('update:transportMethod', value)
    },
    handleButtonClick() {
      // Si es la última entrada del movimiento, hay movimientos anteriores, o es una recepción con campos completos, completar
      if (this.status.status !== 'free' && (this.isLastMovementEntry || this.hasExistingMovements)) {
        this.$emit('submit')
      } else {
        // Caso normal: ir al siguiente paso
        this.$emit('next-step')
      }
    },
    async handleButtonClickEntregar() {
      // Si es la última entrada del movimiento, hay movimientos anteriores, o es una recepción con campos completos, completar
      if (this.status.status !== 'free' && (this.isLastMovementEntry || this.hasExistingMovements)) {
        // Caso 2: NUEVA CONDICIÓN - Vehículo registrado con último movimiento tipo 'entry'
          // Crear un nuevo movimiento de tipo 'cesion' con estado 'reported'
          
          // Determinar el tipo de movimiento basándose en si estamos entregando una cesión o un movimiento normal
          
          let tipo = this.movementType
          
          // Si el último movimiento es una cesión (sin importar su status), el tipo de la entrega debe ser también 'assignment'
          if (this.lastMovement && this.lastMovement.type === 'assignment') {
            tipo = 'assignment'
          }
          
          const movimientoData = {
            type: tipo,
            status: 'reported',
            transportation: this.transportMethod,
            driver_id: this.selectedDriver ? this.selectedDriver.id : null,
            source_location_id: this.initialLocation,
            destination_location_id: this.finalLocation,
            initial_km: this.initialKm,
            vehicle_status: 'good',
            observations: this.damageNotes ? this.damageNotes : null,
            additional_info: this.additionalInfo && this.additionalInfo.trim() !== '' ? this.additionalInfo : null,
            crane_id: this.selectedGrua ? this.selectedGrua.id : null,
            registered: this.driverType === 'MATRICULADO' ? 1 : this.driverType === 'NO MATRICULADO' ? 2 : this.driverType === 'PLACAS ROJAS' ? 0 : null,
            // Usar el id limpio si el actual no es número
            vehicle_id: (typeof this.status.id === 'number' && !isNaN(this.status.id)) ? this.status.id : (this.vehicleIdNumber || parseInt(this.status.id.toString().replace(/_mov_.+$/, ''))),
            datetime: this.datetime,
            end_date: this.endDate, // Usar endDate de la prop
            responsible_id: this.currentUser.id,
            dealer_id: this.isCollaborator ? this.currentDealerId : null
          }

          let response = await MovimientoService.post(movimientoData)
          const { error, data } = response

          if (error) {
            this.$toast.error('Ocurrió un error al procesar el movimiento')
            return
          }

          // Emitir evento para que el componente padre cargue el TransportForm con el nuevo movimiento
          this.$emit('movimiento-entregado', data.data)
      }
    },
    async confirmEntregar() {
      // Verificar si el usuario tiene permiso para aceptar cesión
      if (this.movementType === 'assignment' && !this.HAS_PERMISSION('aceptar_cesion')) {
        this.$toast.error('No tienes permisos para aceptar cesiones')
        this.showConfirmDialog = false
        return
      }
      
      // Cerrar el diálogo
      this.showConfirmDialog = false
      
      // Delegar al TransportForm con la bandera isDelivering activa
      this.$emit('submit-deliver')
    }
  }
}
</script>

<style scoped>
.subtitle-1 {
  color: rgba(0, 0, 0, 0.87);
}
</style>
